import { isGitTracked, isLocalOnly, describeSync } from '../../services/git/gitSyncStatus';
import { isLocalScopeLink } from '../../services/git/scopeLocalGitSync';

/*
 * What the Git page can show (redesign 05.10.2026): every workspace and every
 * scope as one "target", grouped for the pills, each with its state. The
 * detail card renders the selected one.
 *
 * kind: 'linked' (syncs to a remote) · 'local' (tracked on this device) ·
 *       'folder' (its own repository) · 'unlinked' · 'scope'
 */
export const WORKSPACES = 'Workspaces';
export const SCOPES = 'Projects, boards & documents';

/** "maya/bookshop" (or the URL) of a link — what a test or a script finds a pill by. */
const repoOf = (git) => (git?.owner && git?.repo ? `${git.owner}/${git.repo}` : git?.url || '');

const waitingOf = (entry, tone) => (tone === 'waiting' && entry?.changeCount > 0 ? entry.changeCount : 0);

function workspaceTarget(ws, byWorkspace) {
  const entry = byWorkspace?.[String(ws.id)];
  const base = { id: String(ws.id), group: WORKSPACES, name: ws.name, workspace: ws, repo: repoOf(ws.git) };
  if (ws.storage === 'git') return { ...base, kind: 'folder', connected: true, tone: 'idle', waiting: 0 };
  if (!isGitTracked(ws)) return { ...base, kind: 'unlinked', connected: false, tone: 'off', waiting: 0 };
  const local = isLocalOnly(ws);
  const { tone } = describeSync(entry, { local });
  return { ...base, kind: local ? 'local' : 'linked', connected: true, tone, waiting: local ? entry?.changeCount || 0 : waitingOf(entry, tone) };
}

function scopeTarget(scope, byWorkspace) {
  const entry = byWorkspace?.[String(scope.key)];
  const base = { id: String(scope.key), group: SCOPES, name: scope.name, scope, kind: 'scope', team: scope.kind === 'team', repo: repoOf(scope.git) };
  if (!scope.git) return { ...base, connected: false, tone: 'off', waiting: 0 };
  const local = isLocalScopeLink(scope.git);
  const { tone } = describeSync(entry, { local });
  return { ...base, connected: true, tone, waiting: local ? entry?.changeCount || 0 : waitingOf(entry, tone) };
}

const RANK = { linked: 0, local: 0, folder: 1, unlinked: 2 };

/** Workspaces (tracked first, then own folders, then the rest — each in list order), then scopes. */
export function gitTargets(workspaces, scopes, byWorkspace) {
  const ws = (workspaces || []).map((w) => workspaceTarget(w, byWorkspace));
  const ordered = ws.map((t, i) => [t, i]).sort((a, b) => RANK[a[0].kind] - RANK[b[0].kind] || a[1] - b[1]).map(([t]) => t);
  return [...ordered, ...(scopes || []).map((s) => scopeTarget(s, byWorkspace))];
}

/** The one to show: the requested id when it exists, else the first connected, else the first. */
export function pickTarget(targets, id) {
  return targets.find((t) => t.id === String(id ?? '')) || targets.find((t) => t.connected && t.kind !== 'folder') || targets[0] || null;
}
